import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { IAlertComponent, IButtonDirective, ModalService } from "@front/m19-ui";
import { ReactiveFormsModule } from "@angular/forms";
import { TranslocoDirective } from "@jsverse/transloco";
import { AccountSelectionService, TacosStrategiesService } from "@front/m19-services";
import { toSignal } from "@angular/core/rxjs-interop";
import { AsinSelectionComponent } from "@m19-board/shared/asin-selection/asin-selection.component";
import {
  AsinStrategyUsageComponent,
  AsinStrategyUsageModalData,
} from "@m19-board/shared/asin-strategy-usage/asin-strategy-usage-modal.component";
import { FieldTree } from "@angular/forms/signals";

export interface SpCreateStrategyAsinsModel {
  asins: string[];
}

@Component({
  selector: "app-sp-create-strategy-asins",
  imports: [ReactiveFormsModule, TranslocoDirective, AsinSelectionComponent, IAlertComponent, IButtonDirective],
  host: {
    class: "block h-full min-h-0",
  },
  template: `<div class="flex h-full min-h-0 flex-col gap-4 overflow-hidden py-8" *transloco="let t">
    <h4>
      {{ t("common.asins") }}
      <span class="type-body text-muted-foreground">
        ({{ t("common.selected", { count: asins().asins().value().length }) }})
      </span>
    </h4>
    @if (unavailableAsins() && unavailableAsins()!.size) {
      <IAlert class="my-4" type="info" [title]="t('common.asin_used_in_tacos_strategies')">
        <button iButton variant="secondary" size="xs" (click)="openAsinUsageModal()">
          {{ t("common.display_unavailable_asin") }}
        </button>
      </IAlert>
    }
    <div class="min-h-0 flex-1">
      <app-asin-selection
        ineligibilityTag="SP"
        [disabledAsins]="disabledAsins()"
        [csvExport]="false"
        [customList]="customList()"
        [allowOtherCatalog]="allowOtherCatalog()"
        [selectedAsins]="asins().asins().value()"
        (selectedAsinsChange)="asins().asins().value.set($event)"
      />
    </div>
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpCreateStrategyAsinsComponent {
  private readonly tacosStrategyService = inject(TacosStrategiesService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly modalService = inject(ModalService);

  readonly asins = input.required<FieldTree<SpCreateStrategyAsinsModel>>();
  readonly customList = input<string[] | undefined>(undefined);
  readonly allowOtherCatalog = input<boolean>(false);
  readonly excludedAsins = input<string[]>([]);

  private readonly selectedAccount = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });

  unavailableAsins = toSignal(this.tacosStrategyService.unavailableAsinsForSponsoredAds$);
  disabledAsins = computed(() => new Set([...(this.unavailableAsins() ?? []), ...this.excludedAsins()]));

  openAsinUsageModal() {
    this.modalService.openModal<AsinStrategyUsageModalData, void>(AsinStrategyUsageComponent, {
      data: {
        accountId: this.selectedAccount().accountId,
        marketplace: this.selectedAccount().marketplace,
        isTacosStrategy: false,
      },
    });
  }
}
